import { useState } from 'react';

import * as Tabs from '@radix-ui/react-tabs';

import { ReactComponent as FirstTabBg } from '@shared/assets/images/tab/first_tab.svg';
import { ReactComponent as TabBg } from '@shared/assets/images/tab/tabs.svg';

import styles from './TabContentLayout.module.scss';

/**
 * TabContentLayout 컴포넌트
 * - 단일 탭 또는 다중 탭을 지원하는 범용 레이아웃 컴포넌트
 * - 탭 + 컨텐츠 영역을 공통 구조로 제공
 *
 * @param {Object|Array} tabs - 탭 정보
 *   단일 탭: { label: "탭 이름", content: <Component /> }
 *   다중 탭: [{ value: "tab1", label: "탭1", content: <Component /> }, ...]
 * @param {string} defaultTab - 다중 탭일 때 기본 선택 탭 value (선택적)
 * @param {Function} onTabChange - 탭 변경 핸들러 (선택적)
 * @param {boolean} lazyLoad - true일 때 활성 탭만 마운트 (초기 로딩 성능 개선)
 */
export default function TabContentLayout({ tabs, defaultTab, onTabChange, lazyLoad = false }) {
  // 단일 탭인지 다중 탭인지 판단
  const isSingleTab = !Array.isArray(tabs);
  const tabsArray = isSingleTab ? [tabs] : tabs;

  // 다중 탭일 때 상태 관리
  const [activeTab, setActiveTab] = useState(
    defaultTab || tabsArray[0]?.value || tabsArray[0]?.label,
  );

  // 탭 변경 핸들러
  const handleTabChange = (value) => {
    setActiveTab(value);
    if (onTabChange) {
      onTabChange(value);
    }
  };

  // 단일 탭 렌더링
  if (isSingleTab) {
    return (
      <div className={styles.tabContainer}>
        {/* 단일 탭 */}
        <div className={styles.singleTab}>
          <FirstTabBg className={styles.tabBg} />
          <span>{tabs.label}</span>
        </div>

        {/* 컨텐츠 영역 */}
        <div className={styles.contentArea}>{tabs.content}</div>
      </div>
    );
  }

  // 다중 탭 렌더링 (Radix UI Tabs 사용)
  return (
    <div className={styles.tabContainer}>
      <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
        <Tabs.List className={styles.tabsList} data-active-tab={activeTab}>
          {tabsArray.map((tab, index) => {
            const tabValue = tab.value || tab.label;
            const isFirstTab = index === 0;

            return (
              <Tabs.Trigger key={tabValue} className={styles.tabTrigger} value={tabValue}>
                {isFirstTab ? (
                  <FirstTabBg className={styles.tabBg} />
                ) : (
                  <TabBg className={styles.tabBg} />
                )}
                <span>{tab.label}</span>
              </Tabs.Trigger>
            );
          })}
        </Tabs.List>

        {/* 탭 컨텐츠 (lazyLoad 시 활성 탭만 마운트하여 API 호출 감소) */}
        {tabsArray.map((tab) => {
          const tabValue = tab.value || tab.label;
          const isActive = tabValue === activeTab;
          return (
            <Tabs.Content key={tabValue} value={tabValue} className={styles.tabsContentWrapper}>
              {!lazyLoad || isActive ? tab.content : null}
            </Tabs.Content>
          );
        })}
      </Tabs.Root>
    </div>
  );
}
